import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  postLoginAsync,
  postVerify2faAsync,
  postResend2faAsync,
} from "../../../Redux/AuthSlice";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import Alert from "../../../Components/Utils/Alert";
import messageSlice from "../../../Redux/MessageSlice";
import MathCaptchaField from "../../../Components/Utils/MathCaptchaField";

export default function StaffLogin() {
  const [loading, setLoading] = useState(false);
  const [captchaState, setCaptchaState] = useState({
    captcha: "",
    captcha_key: "",
  });
  const [captchaNonce, setCaptchaNonce] = useState(0);
  const [challenge, setChallenge] = useState(null);
  const [otp, setOtp] = useState("");
  const { isLoggedIn } = useSelector((state) => state.auth);
  const { message } = useSelector((state) => state.message);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(messageSlice.actions.clearMessage());
    if (isLoggedIn) {
      navigate("/dashboard");
      window.location.reload();
    }
  }, []);

  const initialValues = {
    username: "",
    password: "",
    panel: "staff",
  };
  const validationSchema = Yup.object().shape({
    username: Yup.string().required("This field is required!"),
    password: Yup.string().required("This field is required!"),
  });

  const finishLogin = () => {
    navigate("/dashboard");
    window.location.reload();
  };

  const handleLogin = (formValue, { resetForm }) => {
    setLoading(true);
    dispatch(messageSlice.actions.clearMessage());
    dispatch(
      postLoginAsync({
        payload: {
          ...formValue,
          captcha: captchaState.captcha,
          captcha_key: captchaState.captcha_key,
        },
      })
    )
      .unwrap()
      .then((data) => {
        if (data?.requires_2fa) {
          setChallenge({
            challenge_token: data.challenge_token,
            channel: data.channel,
            destination_masked: data.destination_masked,
            phone_masked: data.phone_masked,
            email_masked: data.email_masked,
          });
          setOtp("");
          setLoading(false);
          return;
        }
        finishLogin();
      })
      .catch(() => {
        resetForm();
        setCaptchaState({ captcha: "", captcha_key: "" });
        setCaptchaNonce((n) => n + 1);
        setLoading(false);
      });
  };

  const handleVerifyOtp = (e) => {
    e.preventDefault();
    if (!challenge?.challenge_token || !otp.trim()) return;
    setLoading(true);
    dispatch(messageSlice.actions.clearMessage());
    dispatch(
      postVerify2faAsync({
        payload: {
          panel: "staff",
          challenge_token: challenge.challenge_token,
          otp: otp.trim(),
        },
      })
    )
      .unwrap()
      .then(() => finishLogin())
      .catch(() => setLoading(false));
  };

  const handleResendOtp = () => {
    if (!challenge?.challenge_token) return;
    setLoading(true);
    dispatch(messageSlice.actions.clearMessage());
    dispatch(
      postResend2faAsync({
        payload: {
          panel: "staff",
          challenge_token: challenge.challenge_token,
        },
      })
    )
      .unwrap()
      .then((data) => {
        if (data?.message) {
          dispatch(messageSlice.actions.setMessage(data.message));
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const cancelChallenge = () => {
    setChallenge(null);
    setOtp("");
    setCaptchaState({ captcha: "", captcha_key: "" });
    setCaptchaNonce((n) => n + 1);
    dispatch(messageSlice.actions.clearMessage());
  };

  return (
    <>
      <div className='flex items-center justify-center min-h-full px-4 py-12  sm:px-6 lg:px-8'>
        <div className='w-full max-w-md p-10 space-y-8 rounded-lg shadow-2xl'>
          <div>
            <img
              className='w-auto h-12 mx-auto rounded-full'
              src='https://erp.dibru.work/images/dibru.png'
              alt='Logo'
            />

            <h2 className='mt-6 text-3xl font-extrabold text-center text-gray-600'>
              Dibrugarh University Accounts
            </h2>
            <h2 className='mt-1 text-xl font-medium text-center text-gray-600'>
              {challenge
                ? challenge.channel === "email"
                  ? "Enter email OTP"
                  : "Enter mobile OTP"
                : "Login to continue..."}
            </h2>
          </div>
          {message && <Alert message={message} />}

          {challenge ? (
            <form onSubmit={handleVerifyOtp} className='space-y-4'>
              <p className='text-sm text-center text-gray-600'>
                OTP sent to{" "}
                {challenge.destination_masked ||
                  challenge.email_masked ||
                  challenge.phone_masked ||
                  (challenge.channel === "email"
                    ? "your registered email"
                    : "your registered mobile")}
              </p>
              <input
                type='text'
                inputMode='numeric'
                autoComplete='one-time-code'
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                placeholder='Enter 6-digit OTP'
                className='relative block w-full px-3 py-2 text-gray-900 placeholder-gray-500 border border-gray-300 appearance-none focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm'
              />
              <button
                type='submit'
                className='relative flex justify-center w-full px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md group hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                disabled={loading || otp.length < 6}
              >
                Verify OTP
              </button>
              <div className='flex justify-between text-sm'>
                <button
                  type='button'
                  className='text-indigo-600 hover:text-indigo-500'
                  onClick={handleResendOtp}
                  disabled={loading}
                >
                  Resend OTP
                </button>
                <button
                  type='button'
                  className='text-gray-600 hover:text-gray-800'
                  onClick={cancelChallenge}
                  disabled={loading}
                >
                  Back to login
                </button>
              </div>
            </form>
          ) : (
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleLogin}
            >
              <Form>
                <div className='-space-y-px '>
                  <div className=''>
                    <Field
                      name='username'
                      type='text'
                      placeholder='Enter your registered email/contact no.'
                      className='relative block w-full px-3 py-2 text-gray-900 placeholder-gray-500 border border-gray-300 rounded-none appearance-none focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm'
                    />
                    <ErrorMessage
                      name='username'
                      component='div'
                      className='py-2 text-red-500'
                    />
                  </div>

                  <div className=''>
                    <Field
                      name='password'
                      type='password'
                      placeholder='Enter your password'
                      className='relative block w-full px-3 py-2 text-gray-900 placeholder-gray-500 border border-gray-300 rounded-none appearance-none focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm'
                    />
                    <ErrorMessage
                      name='password'
                      component='div'
                      className='py-2 text-red-500'
                    />
                  </div>
                </div>

                <MathCaptchaField
                  key={captchaNonce}
                  panel='staff'
                  value={captchaState}
                  onChange={setCaptchaState}
                />

                <div className='flex items-center justify-end my-10'>
                  <div className='text-sm'>
                    <a
                      href='/forgot-password?panel=staff'
                      className='font-medium text-indigo-600 hover:text-indigo-500'
                    >
                      Forgot Password?
                    </a>
                  </div>
                </div>

                <div>
                  <button
                    type='submit'
                    className='relative flex justify-center w-full px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md group hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                    disabled={loading}
                  >
                    {loading && (
                      <span className='spinner-border spinner-border-sm'></span>
                    )}
                    <span>Login</span>
                  </button>
                </div>
              </Form>
            </Formik>
          )}
        </div>
      </div>
    </>
  );
}
